import { useMemo, useState } from 'react';
import type { ApiClient } from '../../api';
import type { MsrFunktionsvorlage } from './typen';
import {
  ContentSection, DataTable, Drawer, EmptyState, ErrorState, Feld, FormSection,
  StatusBadge, type Spalte,
} from '../../muster';

/**
 * Funktionsvorlagen — gruppiert nach Anlagenart (Vertrag Abschnitt 4.2).
 *
 * Umzug U6c: je Anlagenart eine `DataTable`, Bearbeitung im `Drawer`. Die
 * Gruppierung bleibt: Eine einzige Tabelle über alle Anlagenarten stellt
 * Funktionen nebeneinander, die nie zusammen gelten.
 */
export function Funktionsvorlagen({ api, projektId, vorlagen, onGeaendert }: {
  api: ApiClient; projektId: string; vorlagen: MsrFunktionsvorlage[]; onGeaendert: () => void;
}) {
  const [entwurf, setEntwurf] = useState<MsrFunktionsvorlage | null>(null);
  const [speichertLaeuft, setSpeichertLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const gruppen = useMemo(() => {
    const m = new Map<string, MsrFunktionsvorlage[]>();
    for (const v of vorlagen) {
      const liste = m.get(v.anlagenart) ?? [];
      liste.push(v);
      m.set(v.anlagenart, liste);
    }
    return Array.from(m.entries());
  }, [vorlagen]);

  const bearbeiten = (v: MsrFunktionsvorlage) => { setEntwurf({ ...v }); setFehler(null); };

  const aendern = (teil: Partial<MsrFunktionsvorlage>) =>
    setEntwurf((v) => (v ? { ...v, ...teil } : v));

  const speichern = async () => {
    if (!entwurf) return;
    setSpeichertLaeuft(true);
    setFehler(null);
    try {
      const { id, ...rest } = entwurf;
      await api.msrFunktionsvorlageSetzen(projektId, id, rest);
      setEntwurf(null);
      onGeaendert();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setSpeichertLaeuft(false);
    }
  };

  const spalten: Spalte<MsrFunktionsvorlage>[] = [
    { schluessel: 'kategorie', titel: 'Kategorie', wert: (v) => v.kategorie },
    { schluessel: 'funktion', titel: 'Funktion', wert: (v) => v.funktion, immerSichtbar: true },
    { schluessel: 'kurz', titel: 'Kurz', wert: (v) => v.kurz ?? null },
    { schluessel: 'bedingung', titel: 'Bedingung', wert: (v) => v.bedingung ?? null },
    { schluessel: 'pflicht', titel: 'Pflicht', breite: '90px',
      wert: (v) => (v.pflicht ? 'ja' : 'nein') },
    { schluessel: 'norm', titel: 'Norm', wert: (v) => v.norm ?? null },
    { schluessel: 'begruendung', titel: 'Begründung', wert: (v) => v.begruendung ?? null },
    {
      schluessel: 'istAktiv', titel: 'Zustand', art: 'status', breite: '120px',
      wert: (v) => (v.istAktiv ? 'aktiv' : 'stillgelegt'),
      inhalt: (v) => (v.istAktiv
        ? <StatusBadge klein zustand="gut" text="aktiv" />
        : <StatusBadge klein zustand="neutral" text="stillgelegt" />),
    },
    {
      schluessel: 'aktion', titel: 'Ändern', breite: '120px', sortierbar: false,
      wert: () => '',
      inhalt: (v) => (
        <button type="button" className="m-knopf" onClick={() => bearbeiten(v)}>
          Bearbeiten
        </button>
      ),
    },
  ];

  return (
    <ContentSection
      titel="Funktionsvorlagen"
      beschreibung="Je Anlagenart die GA-Funktionen, die die Ableitung anlegt."
      meta={`${gruppen.length} Anlagenart(en) · ${vorlagen.length} Vorlagen`}
    >
      {gruppen.map(([anlagenart, liste]) => (
        <DataTable
          key={anlagenart}
          name={anlagenart}
          spalten={spalten}
          zeilen={liste}
          schluessel={(v) => v.id}
          sortierung={{ spalte: 'kategorie', richtung: 'auf' }}
          dicht
          suche={false}
          spaltenwahl={false}
          aktiveZeile={entwurf?.id}
          zeilenKlasse={(v) => (v.istAktiv ? undefined : 'schwach')}
          kontextmenue={[{ beschriftung: 'Bearbeiten …', beiWahl: bearbeiten }]}
          leer={<EmptyState klein titel={`Für ${anlagenart} ist keine Vorlage hinterlegt.`} />}
        />
      ))}

      {vorlagen.length === 0 && (
        <EmptyState
          titel="Für dieses Projekt ist keine Funktionsvorlage hinterlegt."
          erklaerung={'Ohne Vorlage leitet der Lauf keine GA-Funktion ab — die Vorlagen '
            + 'kommen aus der Projektvorlage nach VDI 3814.'}
        />
      )}

      <Drawer
        offen={entwurf !== null}
        titel={entwurf ? entwurf.funktion : ''}
        beschreibung={entwurf
          ? `${entwurf.anlagenart} · ${entwurf.kategorie}` : undefined}
        beiSchliessen={() => setEntwurf(null)}
        fuss={(
          <>
            <button type="button" className="m-knopf m-knopf-haupt" disabled={speichertLaeuft}
              onClick={() => void speichern()}>
              {speichertLaeuft ? 'speichert …' : 'Speichern'}
            </button>
            <button type="button" className="m-knopf" onClick={() => setEntwurf(null)}>
              Abbrechen
            </button>
          </>
        )}
      >
        {entwurf && (
          <FormSection titel="Vorlage" spalten={2}>
            <Feld beschriftung="Funktion" wert={entwurf.funktion} breite="voll" pflicht
              beiAenderung={(w) => aendern({ funktion: w as string })} />
            <Feld beschriftung="Kurzzeichen" wert={entwurf.kurz ?? ''}
              beiAenderung={(w) => aendern({ kurz: w as string })} />
            <Feld beschriftung="Bedingung" wert={entwurf.bedingung ?? ''}
              hinweis="Leer heißt: gilt immer."
              beiAenderung={(w) => aendern({ bedingung: w as string })} />
            <Feld beschriftung="Norm" wert={entwurf.norm ?? ''}
              beiAenderung={(w) => aendern({ norm: w as string })} />
            <Feld art="haken" beschriftung="Pflichtfunktion" wert={entwurf.pflicht}
              beiAenderung={(w) => aendern({ pflicht: w as boolean })} />
            <Feld art="schalter" beschriftung="Vorlage aktiv" wert={entwurf.istAktiv}
              beiAenderung={(w) => aendern({ istAktiv: w as boolean })} />
            <Feld beschriftung="Begründung" wert={entwurf.begruendung ?? ''} breite="voll"
              beiAenderung={(w) => aendern({ begruendung: w as string })} />
          </FormSection>
        )}

        {fehler && (
          <ErrorState
            klein
            meldung="Die Funktionsvorlage konnte nicht gespeichert werden."
            technisch={fehler}
            beiErneutVersuchen={() => void speichern()}
          />
        )}
      </Drawer>
    </ContentSection>
  );
}
